"use client";

import * as React from "react";
import { BookOpen, Settings, Plus, Calendar, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CanvasCourse, PlannerItem } from "@/lib/types";
import { CreateNotebookModal } from "./layout/create-notebook-modal";
import { Notebook } from "../client";
import { useNotebookContext } from "../hooks/useNotebook";
import { StudyWorkspace } from "./Workspace";
import { StudySetButton } from "./layout/study-sets/study-set-button";
import { WorkspaceSkeleton } from "./layout/workspace-skeleton";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StudyHero } from "./layout/study-sets/study-hero";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ThemedTabsList, ThemedTabsTrigger } from "./layout/themed-tabs";
import { SuggestedStudyCarousel } from "./layout/study-sets/suggested-study";
import { StudySetGrid } from "./layout/study-sets/study-set-grid";
import { useExams } from "../hooks/useExams";
import { FinalAssignment } from "../types";

interface Assignment {
  id: string;
  name: string;
  course: CanvasCourse;
  dueDate: Date;
}

interface StudyLandingPageProps {
  courses: CanvasCourse[];
  assignments: FinalAssignment[];
}

export function StudyLandingPage({
  courses,
  assignments,
}: StudyLandingPageProps) {
  const [openingNotebookId, setOpeningNotebookId] = React.useState<
    string | null
  >(null);
  // const [landingOpen, setLandingOpen] = React.useState(true);
  const {
    setNotebook,
    notebooks,
    deleteNotebook,
    createNotebook,
    createFlowStep,
    setCreateFlowStep,
  } = useNotebookContext();
  const handleOpenNotebook = async (notebook: Notebook) => {
    if (openingNotebookId) return; // prevent double clicks

    try {
      setOpeningNotebookId(notebook.id.toString());
      await setNotebook(notebook);
    } finally {
      setOpeningNotebookId(null);
    }
  };

  // handle notebook deeplinking
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const notebookId = urlParams.get("notebook_id");
    if (notebookId) {
      const selected = notebooks.find((n) => n.id.toString() === notebookId);
      if (selected) handleOpenNotebook(selected);
    }
  }, []);

  return createFlowStep === "landing" ? (
    <div className="min-h-screen w-full bg-theme-0">
      <div className="min-h-screen bg-theme-0 lg:mx-auto max-w-5xl pt-24">
        <div className="container mx-auto px-6 py-8 max-w-7xl">
          {/* Header */}
          <StudyHero />

          {/* Top-level tabs */}
          <Tabs defaultValue="my" className="mt-20">
            {/* Polished tab header */}
            <div className="relative mb-2">
              {/* full-width horizontal line */}
              <div className="absolute inset-x-0 top-1/2 h-0.5 rounded-full bg-theme-2" />

              {/* centered tabs on top */}
              <div className="relative z-10 flex justify-center">
                <ThemedTabsList>
                  <ThemedTabsTrigger value="my">My Studies</ThemedTabsTrigger>
                  <ThemedTabsTrigger value="community">
                    Community Studies
                  </ThemedTabsTrigger>
                </ThemedTabsList>
              </div>
            </div>

            {/* ================= MY STUDIES ================= */}
            <TabsContent value="my" className="space-y-10">
              <SuggestedStudyCarousel
                assignments={assignments}
                courses={courses}
                onCreateStudySet={(course, name, exam) =>
                  createNotebook({
                    course,
                    name: name ?? `Study: ${course.code || course.default}`,
                    exam: exam,
                  })
                }
              />
              <StudySetGrid
                title="My Study Sets"
                notebooks={notebooks}
                showCreate
                courses={courses}
                onCreate={() => setCreateFlowStep("course")}
                onDelete={deleteNotebook}
                onOpenNotebook={handleOpenNotebook}
              />
            </TabsContent>

            {/* ================= COMMUNITY ================= */}
            <TabsContent value="community">
              <div className="flex min-h-[500px] items-center justify-center rounded-lg border border-dashed">
                <div className="space-y-3 text-center">
                  <BookOpen className="mx-auto h-6 w-6 text-theme-1" />
                  <p className="text-sm text-theme-1">
                    Explore study sets created by other students.
                  </p>
                  <p className="text-xs text-theme-1">Coming soon!</p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  ) : (
    <StudyWorkspace courses={courses} createNotebook={createNotebook} />
  );
}
